/**
 * Time zones: a mosque's zone as data.  OA-017 (first part), OA-010, 2026-09-30.
 *
 * Core computes calendar days and prayer wall-clock times in the runtime's
 * local zone (dates.js explains why), and the API sets that zone once per
 * process. That is correct while every mosque on a platform shares the
 * platform's zone, and silently wrong the day one does not: prayer times a
 * few hours off, in a mosque app.
 *
 * So, until the wall-clock code takes a zone per call (the rest of OA-017):
 *   - a mosque's zone is explicit data (MASJID_ORG_TZ, mosque.fromEnv), shown to
 *     clients in GET /v1/mosque;
 *   - the platform REFUSES a mosque whose zone differs from its own — at add
 *     and at load — rather than serving it wrong times (fail closed);
 *   - code that is not calendar arithmetic (the hour an alert digest goes
 *     out, quiet hours) asks this module for the hour IN the mosque's zone.
 *
 * Intl only; core stays dependency-free.
 */

/** The zone this process computes local time in (the platform's zone). */
function platformZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** The canonical IANA name, or null if it is not a zone this runtime knows. */
function canonical(zone) {
  if (!zone || typeof zone !== 'string') return null;
  try { return new Intl.DateTimeFormat('en-US', { timeZone: zone }).resolvedOptions().timeZone; }
  catch { return null; }
}

function valid(zone) { return canonical(zone) !== null; }

/** The mosque's effective zone: its own, or the platform's. */
function effective(zone, fallback = platformZone()) {
  return canonical(zone) || fallback;
}

/**
 * Whether a mosque in `zone` can be served correctly by this process today:
 * no zone of its own (it takes the platform's), or the same one.
 */
function servable(zone, platform = platformZone()) {
  if (!zone) return true;
  const c = canonical(zone);
  return c !== null && c === canonical(platform);
}

/** Wall-clock fields of an instant in a zone. */
function partsIn(at, zone) {
  const d = at instanceof Date ? at : new Date(at);
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: effective(zone), hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', weekday: 'short',
  });
  const o = {};
  for (const p of f.formatToParts(d)) o[p.type] = p.value;
  return { year: Number(o.year), month: Number(o.month), day: Number(o.day),
           hour: Number(o.hour) % 24, minute: Number(o.minute), weekday: o.weekday };
}

/** The hour (0–23) an instant is in a zone. */
function hourIn(at, zone) { return partsIn(at, zone).hour; }

/** The calendar date (YYYY-MM-DD) an instant is in a zone. */
function dateIn(at, zone) {
  const p = partsIn(at, zone);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/**
 * The instant a mosque-local wall time is: 'YYYY-MM-DD' + 'HH:MM' in `zone` → UTC ISO.  Work hub (2026-10-31): a board
 * meeting made from the planner's «fast møtedag» (a day and a clock time) needs its instant, in the mosque's zone — never
 * the server's. Intl only: the first guess reads the wall as UTC, then the difference from what that instant shows on the
 * wall in `zone` is taken off (twice, for a guess that lands on a clock change). Null for a malformed day or time.
 */
function wallToIso(date, time, zone) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date || ''));
  const t = /^(\d{2}):(\d{2})$/.exec(String(time || ''));
  if (!m || !t) return null;
  const want = { y: +m[1], mo: +m[2], d: +m[3], h: +t[1], mi: +t[2] };
  if (want.mo < 1 || want.mo > 12 || want.d < 1 || want.d > 31 || want.h > 23 || want.mi > 59) return null;
  let ms = Date.UTC(want.y, want.mo - 1, want.d, want.h, want.mi);
  for (let i = 0; i < 2; i++) {
    const p = partsIn(new Date(ms), zone);
    const shown = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    const diff = shown - Date.UTC(want.y, want.mo - 1, want.d, want.h, want.mi);
    if (!diff) break;
    ms -= diff;
  }
  return new Date(ms).toISOString();
}

module.exports = { platformZone, canonical, valid, effective, servable, partsIn, hourIn, dateIn, wallToIso };
